import type { DataSnapshot, Position, RankTier } from '../types';

const tiers: RankTier[] = ['all', 'herald', 'guardian', 'crusader', 'archon', 'legend', 'ancient', 'divine', 'immortal'];

export function validateSnapshot(input: unknown): string[] {
  const errors: string[] = [];
  const snapshot = input as DataSnapshot;
  if (!snapshot || typeof snapshot !== 'object') return ['快照必须是 JSON 对象'];
  if (snapshot.metadata?.schemaVersion !== 3) errors.push('metadata.schemaVersion 必须为 3');
  if (!snapshot.metadata?.generatedAt || Number.isNaN(Date.parse(snapshot.metadata.generatedAt))) errors.push('metadata.generatedAt 无效');
  if (!/^\d+\.\d+[a-z]?$/i.test(snapshot.metadata?.patch ?? '')) errors.push('metadata.patch 无效');
  for (const mode of ['ranked', 'league'] as const) {
    const dataset = snapshot.metadata?.datasets?.[mode];
    if (!dataset || !dataset.source || !dataset.window) errors.push(`metadata.datasets.${mode} 缺失`);
    if (dataset && !['rolling', 'patch'].includes(dataset.scope)) errors.push(`metadata.datasets.${mode}.scope 无效`);
    if (dataset && !['fresh', 'stale', 'cached'].includes(dataset.status)) errors.push(`metadata.datasets.${mode}.status 无效`);
    if (dataset?.patch !== null && !/^\d+\.\d+[a-z]?$/i.test(dataset.patch)) errors.push(`metadata.datasets.${mode}.patch 无效`);
    if (dataset && Number.isNaN(Date.parse(dataset.fetchedAt))) errors.push(`metadata.datasets.${mode}.fetchedAt 无效`);
  }
  if (!Array.isArray(snapshot.heroes) || snapshot.heroes.length < 120) errors.push('英雄数量低于 120');
  const ids = new Set<number>();
  for (const hero of snapshot.heroes ?? []) {
    if (!Number.isInteger(hero.id) || hero.id <= 0) errors.push(`英雄 ID 无效：${hero.id}`);
    if (ids.has(hero.id)) errors.push(`英雄 ID 重复：${hero.id}`);
    ids.add(hero.id);
    if (!hero.nameEn || !hero.nameZh || !hero.slug) errors.push(`英雄 ${hero.id} 名称或 slug 缺失`);
    if (!Array.isArray(hero.positions) || hero.positions.some((p) => !([1, 2, 3, 4, 5] as Position[]).includes(p))) errors.push(`英雄 ${hero.id} 位置无效`);
    const rates = ([1, 2, 3, 4, 5] as Position[]).map((position) => hero.positionRates?.[position]);
    if (rates.some((rate) => typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0 || rate > 100)) errors.push(`英雄 ${hero.id} 位置概率无效`);
    else if (Math.abs(rates.reduce((sum, rate) => sum + rate, 0) - 100) > 0.2) errors.push(`英雄 ${hero.id} 位置概率总和不为 100`);
  }
  const metricGroups = [snapshot.leagueMeta, ...tiers.map((tier) => snapshot.rankMeta?.[tier])].filter(Boolean);
  for (const group of metricGroups) {
    for (const [id, metric] of Object.entries(group ?? {})) {
      if (!ids.has(Number(id))) errors.push(`统计引用未知英雄：${id}`);
      for (const [key, value] of Object.entries(metric)) {
        if (typeof value === 'number' && (value < 0 || value > 100)) errors.push(`${id}.${key} 超出 0–100`);
      }
    }
  }
  if (!Array.isArray(snapshot.matchups)) errors.push('matchups 必须为数组');
  if (!Array.isArray(snapshot.leagueMatchups)) errors.push('leagueMatchups 必须为数组');
  for (const row of [...(snapshot.matchups ?? []), ...(snapshot.leagueMatchups ?? [])]) {
    if (!ids.has(row.heroId) || !ids.has(row.otherHeroId)) errors.push('对局关系引用未知英雄');
    if (row.synergy !== null && Math.abs(row.synergy) > 30) errors.push('协同变化超出 ±30');
    if (row.counter !== null && Math.abs(row.counter) > 30) errors.push('克制变化超出 ±30');
  }
  if (!Array.isArray(snapshot.laneMatchups)) errors.push('laneMatchups 必须为数组');
  for (const row of snapshot.laneMatchups ?? []) {
    if (!ids.has(row.heroId) || !ids.has(row.otherHeroId)) errors.push('真实对线关系引用未知英雄');
    if (![1, 2, 3, 4, 5].includes(row.position) || ![1, 2, 3, 4, 5].includes(row.otherPosition)) errors.push('真实对线关系位置无效');
    if (!Number.isFinite(row.advantage) || Math.abs(row.advantage) > 30) errors.push('真实对线优势超出 ±30');
  }
  return [...new Set(errors)];
}

export async function loadSnapshot(fetcher: typeof fetch = fetch): Promise<DataSnapshot> {
  const response = await fetcher('/data/snapshot.json');
  if (!response.ok) throw new Error(`数据快照加载失败：${response.status}`);
  const snapshot = await response.json() as DataSnapshot;
  const errors = validateSnapshot(snapshot);
  if (errors.length) throw new Error(errors.join('；'));
  return snapshot;
}
